import type { Status } from "@/types/deal";
import { isPipelineViewId, type PipelineViewId } from "./pipelineViews";

const STORAGE_KEY = "badger.pipeline.preferences.v1";

export type PipelinePreferences = {
  view: PipelineViewId;
  /** Columns of the Custom view, in order. */
  customColumns: string[];
  activeOnly: boolean;
  /** Only rows where the user is deal lead or support. */
  myDealsOnly: boolean;
  statusFilter: Status[];
  countryFilterIds: (string | number)[];
  sectorFilterIds: (string | number)[];
  capitalSourceFilterIds: (string | number)[];
  sourceFilterIds: (string | number)[];
};

export const DEFAULT_PIPELINE_PREFERENCES: PipelinePreferences = {
  view: "meeting",
  customColumns: [],
  activeOnly: true,
  myDealsOnly: false,
  statusFilter: [],
  countryFilterIds: [],
  sectorFilterIds: [],
  capitalSourceFilterIds: [],
  sourceFilterIds: [],
};

const isIdArray = (value: unknown): value is (string | number)[] =>
  Array.isArray(value) &&
  value.every((v) => typeof v === "string" || typeof v === "number");

type StoredPreferences = Partial<PipelinePreferences> & {
  /** Before views: Condensed (true) / Expanded (false). */
  meetingView?: unknown;
};

function storedView(parsed: StoredPreferences): PipelineViewId {
  if (isPipelineViewId(parsed.view)) return parsed.view;
  if (parsed.meetingView === true) return "meeting";
  if (parsed.meetingView === false) return "full";
  return DEFAULT_PIPELINE_PREFERENCES.view;
}

/** Per-browser view and filter choices; storage may be unavailable, so fall back to defaults. */
export function loadPipelinePreferences(): PipelinePreferences {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PIPELINE_PREFERENCES;
    const parsed = JSON.parse(raw) as StoredPreferences;
    return {
      view: storedView(parsed),
      customColumns: Array.isArray(parsed.customColumns)
        ? parsed.customColumns.filter((c): c is string => typeof c === "string")
        : [],
      activeOnly:
        typeof parsed.activeOnly === "boolean"
          ? parsed.activeOnly
          : DEFAULT_PIPELINE_PREFERENCES.activeOnly,
      myDealsOnly:
        typeof parsed.myDealsOnly === "boolean"
          ? parsed.myDealsOnly
          : DEFAULT_PIPELINE_PREFERENCES.myDealsOnly,
      statusFilter: Array.isArray(parsed.statusFilter)
        ? parsed.statusFilter.filter(
            (s): s is Status =>
              !!s && typeof s === "object" && typeof s.id === "number",
          )
        : [],
      countryFilterIds: isIdArray(parsed.countryFilterIds)
        ? parsed.countryFilterIds
        : [],
      sectorFilterIds: isIdArray(parsed.sectorFilterIds)
        ? parsed.sectorFilterIds
        : [],
      capitalSourceFilterIds: isIdArray(parsed.capitalSourceFilterIds)
        ? parsed.capitalSourceFilterIds
        : [],
      sourceFilterIds: isIdArray(parsed.sourceFilterIds)
        ? parsed.sourceFilterIds
        : [],
    };
  } catch {
    return DEFAULT_PIPELINE_PREFERENCES;
  }
}

export function savePipelinePreferences(preferences: PipelinePreferences) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    // Private mode / blocked storage: preferences just won't persist.
  }
}
